/**
 * Risk-based auth + WebAuthn/Passkey contracts (phase 0+1).
 */
import { GHALISHOR_API_V1_PREFIX } from './identity-access';
import type { IsoDateTimeString, UuidString } from './identity-access';

export const AUTH_POLICY_STATUSES = Object.freeze(['draft', 'published', 'archived'] as const);
export type AuthPolicyStatus = (typeof AUTH_POLICY_STATUSES)[number];

export const AUTH_PRIMARY_METHODS = Object.freeze([
  'passkey',
  'password',
  'otp',
  'magic_link',
] as const);
export type AuthPrimaryMethod = (typeof AUTH_PRIMARY_METHODS)[number];

export const WEBAUTHN_CREDENTIAL_STATUSES = Object.freeze(['active', 'disabled'] as const);
export type WebAuthnCredentialStatus = (typeof WEBAUTHN_CREDENTIAL_STATUSES)[number];

export const PASSKEY_AUTH_ROUTES = Object.freeze({
  policy: `${GHALISHOR_API_V1_PREFIX}/auth/policy`,
  registerOptions: `${GHALISHOR_API_V1_PREFIX}/auth/passkey/register/options`,
  registerVerify: `${GHALISHOR_API_V1_PREFIX}/auth/passkey/register/verify`,
  loginOptions: `${GHALISHOR_API_V1_PREFIX}/auth/passkey/login/options`,
  loginVerify: `${GHALISHOR_API_V1_PREFIX}/auth/passkey/login/verify`,
  list: `${GHALISHOR_API_V1_PREFIX}/auth/passkeys`,
  update: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/auth/passkeys/${encodeURIComponent(id)}`,
  revoke: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/auth/passkeys/${encodeURIComponent(id)}`,
  recoveryStatus: `${GHALISHOR_API_V1_PREFIX}/auth/recovery/status`,
  recoveryGenerate: `${GHALISHOR_API_V1_PREFIX}/auth/recovery/generate`,
  recoveryVerify: `${GHALISHOR_API_V1_PREFIX}/auth/recovery/verify`,
} as const);

export interface AuthPolicySummary {
  readonly id: UuidString;
  readonly key: string;
  readonly nameFa: string;
  readonly status: AuthPolicyStatus;
  readonly version: number;
  readonly primaryAuthMethod: AuthPrimaryMethod;
  readonly fallbackAuthMethod: AuthPrimaryMethod;
  readonly passkeyRequired: boolean;
  readonly minimumPasskeys: number;
  readonly passwordAllowed: boolean;
  readonly magicLinkAllowed: boolean;
  readonly recoveryCodeAllowed: boolean;
  readonly otpAllowed: boolean;
  readonly trustedDeviceEnabled: boolean;
  readonly sessionMaxAgeSeconds: number;
  readonly sensitiveActionReauthentication: boolean;
  readonly accountLockThreshold: number;
  readonly accountLockDurationSeconds: number;
}

export interface ResolvedAuthPolicyResponse {
  readonly enabled: boolean;
  readonly policy: AuthPolicySummary;
  readonly passkeyCount: number;
  readonly canRegisterPasskey: boolean;
  readonly canLoginWithPasskey: boolean;
  readonly suggestPasskeyEnrollment: boolean;
  /** Phase 2: role requires more passkeys before sensitive/platform console. */
  readonly enrollmentRequired: boolean;
  readonly passkeysNeeded: number;
  readonly canAccessSensitiveConsole: boolean;
  readonly recoveryCodesRemaining: number;
}

export interface RecoveryStatusResponse {
  readonly allowed: boolean;
  readonly remaining: number;
  readonly hasActiveBatch: boolean;
}

export interface GenerateRecoveryCodesResponse {
  readonly codes: readonly string[];
  readonly batchId: UuidString;
  readonly createdAt: IsoDateTimeString;
}

export interface RecoveryVerifyRequest {
  readonly mobile: string;
  readonly code: string;
}

export interface PasskeyCredentialSummary {
  readonly id: UuidString;
  readonly deviceName: string;
  readonly createdAt: IsoDateTimeString;
  readonly lastUsedAt: IsoDateTimeString | null;
  readonly transports: readonly string[];
  readonly status: WebAuthnCredentialStatus;
}

export interface ListPasskeysResponse {
  readonly items: readonly PasskeyCredentialSummary[];
  readonly minimumRequired: number;
}

export interface UpdatePasskeyRequest {
  readonly deviceName?: string;
  readonly status?: WebAuthnCredentialStatus;
}

export interface PasskeyLoginOptionsRequest {
  readonly mobile: string;
}

/** PublicKeyCredentialCreationOptionsJSON from SimpleWebAuthn (opaque to clients). */
export type PublicKeyCredentialCreationOptionsJSON = Record<string, unknown>;
export type PublicKeyCredentialRequestOptionsJSON = Record<string, unknown>;
export type RegistrationResponseJSON = Record<string, unknown>;
export type AuthenticationResponseJSON = Record<string, unknown>;

export interface PasskeyRegisterOptionsResponse {
  readonly options: PublicKeyCredentialCreationOptionsJSON;
}

export interface PasskeyRegisterVerifyRequest {
  readonly response: RegistrationResponseJSON;
  readonly deviceName?: string;
}

export interface PasskeyRegisterVerifyResponse {
  readonly credential: PasskeyCredentialSummary;
}

export interface PasskeyLoginOptionsResponse {
  readonly options: PublicKeyCredentialRequestOptionsJSON;
  readonly available: boolean;
}

export interface PasskeyLoginVerifyRequest {
  readonly mobile: string;
  readonly response: AuthenticationResponseJSON;
}

export interface PasskeyLoginVerifyResponse {
  readonly accessToken: string;
  readonly accessExpiresAt: IsoDateTimeString;
  readonly refreshToken?: string;
  readonly user: {
    readonly id: UuidString;
    readonly mobileE164: string;
    readonly status: 'active' | 'inactive';
    readonly createdAt: IsoDateTimeString;
  };
  readonly memberships: readonly import('./identity-access').IdentityMembershipSummary[];
}
